import { useEffect, useState } from 'react';
import type { ApiClient, HeizungDaemmnachweisliste } from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, KpiCard, KpiReihe, formatiereZahl,
  LoadingState, StatusBadge, type Spalte, type Zustand,
} from '../muster';

/**
 * Der Dämmnachweis nach GEG Anlage 8 und gegen den Taupunkt.
 *
 * ═══ Drei Zahlen, und die dritte steht nicht klein gedruckt ═══════════════
 *
 * erfüllt · unterschritten · **nicht beurteilbar**. Die dritte Klasse ist
 * keine Restgröße: Eine Leitung ohne Dämmstoffangabe ist nicht gedämmt und
 * nicht ungedämmt — sie ist ungeprüft.
 *
 * Deshalb steht sie als eigene Karte NEBEN den beiden anderen und nicht als
 * Fußnote darunter, und deshalb heißt die vierte Karte „beurteilt" und nicht
 * „Vollständigkeit": Bei der Heizlast meldet die Kopfzeile 100 % Vollständig-
 * keit, während 206 Bauteile mit 0 W in die Bilanz gehen. Genau diese
 * Verwechslung soll hier nicht möglich sein.
 *
 * ═══ Warum die Arbeitsliste die erfüllten nicht zeigt ═════════════════════
 *
 * Weil sie eine Arbeitsliste ist. Wer die erfüllten sehen will, wählt sie
 * ausdrücklich — die Voreinstellung sind die unterschrittenen und die
 * ungeprüften.
 */
export function Daemmnachweisansicht({ api, projektId }: {
  api: ApiClient;
  projektId: string;
}) {
  const [daten, setDaten] = useState<HeizungDaemmnachweisliste | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [urteil, setUrteil] = useState<string>('');

  useEffect(() => {
    let abgebrochen = false;

    api.heizungDaemmung(projektId, {
      urteil: urteil === '' ? undefined : urteil, grenze: 300,
    })
      .then((d) => { if (!abgebrochen) { setDaten(d); setFehler(null); } })
      .catch((e) => {
        if (!abgebrochen) setFehler(e instanceof Error ? e.message : String(e));
      });

    return () => { abgebrochen = true; };
  }, [api, projektId, urteil]);

  if (fehler) {
    return (
      <ContentSection titel="Dämmnachweis GEG Anlage 8">
        <ErrorState
          klein
          meldung="Der Dämmnachweis konnte nicht geladen werden."
          technisch={fehler}
        />
      </ContentSection>
    );
  }

  if (!daten) {
    return (
      <ContentSection titel="Dämmnachweis GEG Anlage 8">
        <LoadingState text="Dämmnachweis wird geladen …" zeilen={5} spalten={5} />
      </ContentSection>
    );
  }

  const gesamt = daten.nachweis.find((n) => n.gattung === 'gesamt');
  const jeKreis = daten.nachweis.filter((n) => n.gattung === 'kreis');
  const jeGrund = daten.nachweis.filter((n) => n.gattung === 'grund');
  const jeStoff = daten.nachweis.filter((n) => n.gattung === 'daemmstoff');

  const unbestaetigt = daten.ansaetze.filter((a) => !a.istBestaetigt).length;
  const ungeprueft = daten.klassen.filter((k) => !k.geprueft).length;

  const zahl = (n: number | null | undefined, z = 0) => formatiereZahl(n, z);

  const urteilZustand = (u: string | null): Zustand =>
    (u === 'erfuellt' ? 'gut'
      : u === 'unterschritten' ? 'fehler' : 'warnung');

  const urteilText = (u: string | null) =>
    (u === 'erfuellt' ? 'erfüllt'
      : u === 'unterschritten' ? 'unterschritten'
        : u === 'nicht_beurteilbar' ? 'nicht beurteilbar' : '—');

  const spalten: Spalte<HeizungDaemmnachweisliste['liste'][number]>[] = [
    {
      schluessel: 'element', titel: 'Element', immerSichtbar: true,
      wert: (z) => z.revitElementId,
      inhalt: (z) => (
        <>
          {z.revitElementId}
          <span className="schwach"> {z.art}</span>
        </>
      ),
    },
    { schluessel: 'kreis', titel: 'Kreis', wert: (z) => z.kreis },
    { schluessel: 'geschoss', titel: 'Geschoss', wert: (z) => z.geschoss },
    {
      schluessel: 'dn', titel: 'DN', art: 'zahl', wert: (z) => z.nennweite,
    },
    {
      schluessel: 'daemmstoff', titel: 'Dämmstoff',
      wert: (z) => z.daemmungstyp,
      inhalt: (z) => (z.daemmungstyp
        ? <span title={z.daemmungstyp}>{z.daemmungstyp}</span>
        : <StatusBadge zustand="warnung" text="keiner" klein />),
    },
    {
      schluessel: 'lambda', titel: 'λ', einheit: 'W/(m·K)', art: 'zahl',
      nachkomma: 3, wert: (z) => z.lambdaWmK,
      titelHinweis: 'Angesetzte Wärmeleitfähigkeit aus heizung_daemmklassen. '
        + 'Ohne sie ist die Anlage-8-Dicke nicht umzurechnen.',
    },
    {
      schluessel: 'zeile', titel: 'Fundstelle', wert: (z) => z.zeile,
      inhalt: (z) => <span className="sehr-schwach">{z.zeile ?? '—'}</span>,
    },
    {
      schluessel: 'tabelle', titel: 'Tabelle', art: 'zahl', einheit: 'mm',
      nachkomma: 0, wert: (z) => z.sollTabelleMm,
      titelHinweis: 'Die Dicke der Anlage-8-Zeile bei 0,035 W/(m·K)',
    },
    {
      schluessel: 'geg', titel: 'GEG umgerechnet', art: 'zahl', einheit: 'mm',
      nachkomma: 1, wert: (z) => z.sollGegMm,
      titelHinweis: 'Dieselbe Zeile, auf das λ des verbauten Dämmstoffs '
        + 'umgerechnet',
    },
    {
      schluessel: 'taupunkt', titel: 'Taupunkt erf.', art: 'zahl',
      einheit: 'mm', nachkomma: 1, wert: (z) => z.sollTaupunktMm,
      titelHinweis: 'Dicke, bei der die Oberfläche über dem Taupunkt bleibt',
    },
    {
      schluessel: 'soll', titel: 'maßgebend', art: 'zahl', einheit: 'mm',
      nachkomma: 1, wert: (z) => z.sollMm,
      inhalt: (z) => <strong>{zahl(z.sollMm, 1)}</strong>,
    },
    {
      schluessel: 'ist', titel: 'verbaut', art: 'zahl', einheit: 'mm',
      nachkomma: 0, wert: (z) => z.vorhandenMm,
    },
    {
      schluessel: 'verhaeltnis', titel: 'ist/soll', art: 'zahl', nachkomma: 2,
      wert: (z) => z.verhaeltnis,
    },
    {
      schluessel: 'urteil', titel: 'Urteil', art: 'status', breite: '190px',
      immerSichtbar: true,
      wert: (z) => urteilText(z.urteil),
      inhalt: (z) => (
        <>
          <StatusBadge zustand={urteilZustand(z.urteil)}
                       text={urteilText(z.urteil)} klein />
          {z.grund && (
            <span className="schwach"> {z.grund}</span>
          )}
        </>
      ),
    },
  ];

  return (
    <ContentSection
      titel={'Dämmnachweis: GEG Anlage 8 und der Taupunkt'}
      aufklappbar
      standardOffen={(gesamt?.nichtBeurteilbarAnzahl ?? 0) > 0
                  || (gesamt?.unterschrittenAnzahl ?? 0) > 0}
      meta={(
        <>
          {(gesamt?.nichtBeurteilbarAnzahl ?? 0) > 0 && (
            <StatusBadge zustand="warnung" klein
              text={`${zahl(gesamt!.nichtBeurteilbarAnzahl)} nicht beurteilbar`} />
          )}
          {(gesamt?.unterschrittenAnzahl ?? 0) > 0 && (
            <StatusBadge zustand="fehler" klein
              text={`${zahl(gesamt!.unterschrittenAnzahl)} unterschritten`} />
          )}
        </>
      )}
    >
      {!gesamt && (
        <EmptyState
          titel="Der Dämmnachweis wurde noch nicht gerechnet."
          erklaerung={'Werkzeug: daemmung-pruefen <projektId> --schreiben. '
            + 'Ohne --schreiben wird nichts abgelegt.'}
        />
      )}

      {gesamt && (
        <>
          <KpiReihe>
            <KpiCard
              titel="erfüllt"
              wert={gesamt.erfuelltAnzahl}
              vergleich={`von ${zahl(gesamt.anzahl)} Leitungen und Formstücken`}
              zustand="gut"
            />
            <KpiCard
              titel="unterschritten"
              wert={gesamt.unterschrittenAnzahl}
              vergleich={gesamt.fehlmengeMittelMm === null ? undefined
                : `im Mittel ${zahl(gesamt.fehlmengeMittelMm, 1)} mm zu dünn`}
              zustand={gesamt.unterschrittenAnzahl > 0 ? 'fehler' : 'gut'}
              zustandText={gesamt.unterschrittenAnzahl > 0
                ? 'unter der Mindestdicke' : 'keine'}
            />
            <KpiCard
              titel="nicht beurteilbar"
              wert={gesamt.nichtBeurteilbarAnzahl}
              vergleich="ungeprüft — weder erfüllt noch unterschritten"
              zustand={gesamt.nichtBeurteilbarAnzahl > 0 ? 'warnung' : 'gut'}
              zustandText={gesamt.nichtBeurteilbarAnzahl > 0
                ? 'Arbeitsliste' : 'vollständig'}
              fussnote="Eine leere Spalte ist kein Befund"
            />
            <KpiCard
              titel="beurteilt"
              wert={gesamt.beurteilbarProzent}
              einheit="%" nachkomma={1}
              vergleich="Anteil mit Urteil — KEINE Erfüllungsquote"
              fussnote={`Taupunkt ${zahl(gesamt.taupunktC, 1)} °C`}
            />
          </KpiReihe>

          <p className="schwach">
            Die Mindestdicken der Anlage 8 gelten für λ = 0,035 W/(m·K); für
            jeden anderen Dämmstoff wird umgerechnet, und die angesetzte
            Leitfähigkeit steht an jeder Zeile. Gerechnet wird durchgehend
            „frei verlegt" — welche Meter im Durchbruch, zwischen Nutzern oder
            im Fußbodenaufbau liegen (Zeilen 5 bis 7, halbe Dicke), führt der
            Planungsstand nicht.
            {unbestaetigt > 0 && (
              <> <strong>{unbestaetigt} von {daten.ansaetze.length} Ansätzen
              der Taupunktrechnung sind unbestätigt</strong>, {ungeprueft} von
              {' '}{daten.klassen.length} Wärmeleitfähigkeiten ungeprüft.</>
            )}
          </p>

          <p>
            <span className="schwach">Ansätze: </span>
            {daten.ansaetze.map((a) => (
              <StatusBadge key={a.schluessel} klein zustand={a.istBestaetigt ? 'gut' : 'warnung'}
                text={`${a.bezeichnung}: ${zahl(a.wert, 1)} ${a.einheit ?? ''}`
                  + `${a.istBestaetigt ? '' : ' (unbestätigt)'} · ${a.quelle ?? 'ohne Quelle'}`} />
            ))}
          </p>

          {jeGrund.length > 0 && (
            <p>
              <span className="schwach">Warum nicht beurteilbar: </span>
              {jeGrund.map((g) => (
                <StatusBadge key={g.schluessel} zustand="warnung" klein
                  text={`${g.bezeichnung}: ${zahl(g.anzahl)}`} />
              ))}
            </p>
          )}

          {jeKreis.length > 0 && (
            <p>
              <span className="schwach">Je Kreis (erfüllt / unterschritten / nicht beurteilbar): </span>
              {jeKreis.map((k) => (
                <StatusBadge key={k.schluessel} klein zustand="neutral"
                  text={`${k.bezeichnung}: ${zahl(k.erfuelltAnzahl)} / ${zahl(k.unterschrittenAnzahl)} / `
                    + `${zahl(k.nichtBeurteilbarAnzahl)}${k.befund ? ` · ${k.befund}` : ''}`} />
              ))}
            </p>
          )}

          {jeStoff.length > 0 && (
            <p>
              <span className="schwach">Je Dämmstoff: </span>
              {jeStoff.map((s) => (
                <StatusBadge key={s.schluessel} klein zustand="neutral"
                  text={`${s.bezeichnung}: ${zahl(s.anzahl)}${s.befund ? ` · ${s.befund}` : ''}`} />
              ))}
            </p>
          )}

          <p>
            <span className="schwach">Arbeitsliste: </span>
            {['', 'unterschritten', 'nicht_beurteilbar', 'erfuellt'].map((u) => (
              <button key={u || 'offen'}
                      className={urteil === u ? 'm-knopf m-knopf-haupt' : 'm-knopf'}
                      onClick={() => setUrteil(u)}>
                {u === '' ? 'offene Punkte' : urteilText(u)}
              </button>
            ))}
          </p>

          <DataTable
            name="Dämmnachweis"
            spalten={spalten}
            zeilen={daten.liste}
            schluessel={(z) => String(z.id)}
            dicht
            ausfuhr
            ausfuhrDateiname="daemmnachweis"
            leer={(
              <EmptyState
                titel="Keine Zeile in dieser Klasse."
                erklaerung="Die Zahlen oben gelten für den ganzen Bestand."
              />
            )}
          />

          <p className="schwach">
            Die Liste ist auf 300 Zeilen begrenzt und nach dem Verhältnis
            ist/soll sortiert — die ungeprüften zuerst, dann die am weitesten
            unterschrittenen. Die Zahlen oben zählen alle
            {' '}{zahl(gesamt.anzahl)} Leitungen und Formstücke.
          </p>
        </>
      )}
    </ContentSection>
  );
}
